import { convertToDbDate, removeCommas } from '@/core/js/common';
import { AuthService } from '@/service/AuthService';

function validateDateRange(value) {
    if (!value) return true; // Let 'required' handle empty values
    // Check the format: dd/mm/yyyy - dd/mm/yyyy
    const rangeRegex = /^(\d{2}\/\d{2}\/\d{4})\s*-\s*(\d{2}\/\d{2}\/\d{4})$/;
    const match = value.match(rangeRegex);
    if (!match) return 'Date range must be in dd/mm/yyyy - dd/mm/yyyy format';
    return true;
}

function validateDateFormat(value) {
    if (!value) return true; // Let 'required' handle empty values

    // Normalize format
    const normalizedFormat = 'dd/mm/yyyy';

    if (normalizedFormat !== 'dd/mm/yyyy') {
        console.warn(`Only 'dd/MM/yyyy' format is currently supported`);
        return true;
    }

    // Format validation
    if (!/^(\d{2})\/(\d{2})\/(\d{4})$/.test(value)) {
        return 'Date must be in DD/MM/YYYY format';
    }

    const [day, month, year] = value.split('/').map(Number);

    // Basic validation
    if (year < 1000 || year > 9999) return 'Year must be between 1000-9999';
    if (month < 1 || month > 12) return 'Month must be between 01-12';

    // Day validation
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) {
        return `Day must be between 01-${daysInMonth} for ${month}/${year}`;
    }

    // Date validity
    const inputDate = new Date(year, month - 1, day);
    if (inputDate.getFullYear() !== year || inputDate.getMonth() !== month - 1 || inputDate.getDate() !== day) {
        return 'Invalid date';
    }

    // Future date check
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to compare dates only
    if (inputDate > today) {
        return 'Date cannot be in the future';
    }

    return true;
}

function validateDateFormatOnly(value) {
    if (!value) return true; // Let 'required' handle empty values

    // Normalize format
    const normalizedFormat = 'dd/mm/yyyy';

    if (normalizedFormat !== 'dd/mm/yyyy') {
        console.warn(`Only 'dd/MM/yyyy' format is currently supported`);
        return true;
    }

    // Format validation
    if (!/^(\d{2})\/(\d{2})\/(\d{4})$/.test(value)) {
        return 'Date must be in DD/MM/YYYY format';
    }

    const [day, month, year] = value.split('/').map(Number);

    // Basic validation
    if (year < 1000 || year > 9999) return 'Year must be between 1000-9999';
    if (month < 1 || month > 12) return 'Month must be between 01-12';

    // Day validation
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) {
        return `Day must be between 01-${daysInMonth} for ${month}/${year}`;
    }

    // Date validity
    const inputDate = new Date(year, month - 1, day);
    if (inputDate.getFullYear() !== year || inputDate.getMonth() !== month - 1 || inputDate.getDate() !== day) {
        return 'Invalid date';
    }

    return true;
}

function validateDatePeriod(value) {
    const period = AuthService.getAccountingPeriod();
    const start_date = period.start_date;
    const end_date = period.end_date;
    if (!value) return true;
    value = convertToDbDate(value);
    if (value >= start_date && value <= end_date) {
        return true;
    } else {
        return 'Date must be within the current accounting period';
    }
}

function getPrimeVueValue(el) {
    //  console.log('Getting value for:', el.classList.toString());

    // Dropdown/Select - Enhanced detection
    if (el.classList.contains('p-dropdown') || el.classList.contains('p-select')) {
        // Method 1: Check for actual selected value in PrimeVue data attributes
        const hiddenInput = el.querySelector('input[type="hidden"]');
        if (hiddenInput) {
            //console.log('Hidden input found:', hiddenInput.value);
            if (hiddenInput.value) return hiddenInput.value;
        }

        // Method 2: Check for data-pc-value attribute (PrimeVue internal)
        const pcValue = el.getAttribute('data-pc-value');
        if (pcValue) {
            // console.log('PC Value found:', pcValue);
            return pcValue;
        }

        // Method 3: Check for selected option with specific classes
        const selectedLabel = el.querySelector('.p-dropdown-label');
        if (selectedLabel) {
            const labelText = selectedLabel.textContent.trim();
            const isPlaceholder = selectedLabel.classList.contains('p-placeholder') || labelText === 'Select' || labelText === 'Select...' || labelText === '';

            if (!isPlaceholder) {
                // console.log('Selected label:', labelText);
                return labelText;
            }
        }

        // Method 4: Check for active descendant in dropdown panel
        const dropdownPanel = document.querySelector('.p-dropdown-panel');
        if (dropdownPanel) {
            const activeItem = dropdownPanel.querySelector('.p-highlight');
            if (activeItem) {
                const activeValue = activeItem.getAttribute('data-value') || activeItem.textContent.trim();
                // console.log('Active item value:', activeValue);
                return activeValue;
            }
        }

        // Method 5: Check if dropdown has filled class but no value
        if (el.classList.contains('p-inputwrapper-filled')) {
            // This means something is selected but we can't get the value
            // Return a non-empty string to pass required validation
            return 'selected'; // This will pass required validation
        }

        //console.log('No dropdown value detected');
        return '';
    }

    // Multiselect
    else if (el.classList.contains('p-multiselect')) {
        const tokens = el.querySelectorAll('.p-multiselect-token-label');
        if (tokens.length > 0) {
            const values = Array.from(tokens).map((token) => token.textContent.trim());
            //  console.log('Multiselect values:', values);
            return values;
        }

        // Check if multiselect has filled class
        if (el.classList.contains('p-inputwrapper-filled')) {
            return ['selected']; // Pass required validation
        }

        return [];
    }

    // Password
    else if (el.classList.contains('p-password')) {
        const input = el.querySelector('input');
        const value = input?.value || '';
        // console.log('Password value:', value);
        return value;
    }

    // Calendar/DatePicker
    else if (el.classList.contains('p-calendar') || el.classList.contains('p-datepicker')) {
        const input = el.querySelector('input');
        const value = input?.value || '';
        // console.log('Calendar value:', value);
        return value;
    }

    // InputText and other inputs
    else if (el.classList.contains('p-inputtext') || el.tagName === 'INPUT') {
        const value = el.value?.trim() || el.value || '';
        // console.log('Input value:', value);
        return value;
    }

    // Textarea
    else if (el.tagName === 'TEXTAREA') {
        const value = el.value?.trim() || el.value || '';
        //  console.log('Textarea value:', value);
        return value;
    }

    // Fallback
    const value = el.value || '';
    //console.log('Fallback value:', value);
    return value;
}

export function validateField(el, binding, isUserInteraction = false) {
    // Agar automatic listener (keyup/input/blur) hai aur field touched nahi hai, toh return true
    if (isUserInteraction && !el.__touched) {
        return true;
    }

    // Submit ke waqt (jab direct validateField call hota hai) tou field ko touched mark kar dein
    el.__touched = true;

    let value = getPrimeVueValue(el);

    const rules = binding.value?.rules || el.__rules || [];
    const errorElement = el.nextElementSibling;

    let error = '';
    for (const rule of rules) {
        if (rule === 'required' && (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0))) {
            error = 'This field is required';
            break;
        }

        if (rule.startsWith('data-only-numbers')) {
            if (value !== null && value !== undefined && value.toString().trim() !== '') {
                const num = parseFloat(value.toString().replace(/,/g, ''));
                if (isNaN(num)) {
                    error = `Only numbers are allowed`;
                    break;
                }
                if (num < 0) {
                    error = `Only positive numbers are allowed`;
                    break;
                }
            }
        }
        if (rule.startsWith('greater-than')) {
            const num = removeCommas(value);
            const min = Number(rule.split(':')[1]);
            if (isNaN(num) || num <= min) {
                error = `Must be greater than ${min}`;
                break;
            }
        }
        if (rule.startsWith('equall-to')) {
            const num = removeCommas(value);
            const min = Number(rule.split(':')[1]);
            if (isNaN(num) || num != min) {
                error = `Must be equall to ${min}`;
                break;
            }
        }

        if (rule === 'date-format') {
            const dateValidationResult = validateDateFormat(value);
            if (dateValidationResult !== true) {
                error = dateValidationResult;
                break;
            }
        }
        if (rule === 'date-format-only') {
            const dateValidationResult = validateDateFormatOnly(value);
            if (dateValidationResult !== true) {
                error = dateValidationResult;
                break;
            }
        }
        if (rule === 'date-range') {
            const dateValidationResult = validateDateRange(value);
            if (dateValidationResult !== true) {
                error = dateValidationResult;
                break;
            }
        }
        if (rule === 'date-period') {
            const dateValidationResult = validateDatePeriod(value);
            if (dateValidationResult !== true) {
                error = dateValidationResult;
                break;
            }
        }
        if (rule === 'email') {
            if (value && value.trim() !== '') {
                const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailPattern.test(value)) {
                    error = 'Please enter a valid email address';
                    break;
                }
            }
        }
    }

    // UI Error handling
    if (errorElement && errorElement.classList.contains('validation-error')) {
        if (error) {
            errorElement.textContent = error;
        } else {
            errorElement.remove();
        }
    } else if (error) {
        const span = document.createElement('span');
        span.className = 'validation-error text-red-500 text-sm';
        span.textContent = error;
        el.after(span);
    }

    if (error) {
        el.classList.add('p-invalid');
    } else {
        el.classList.remove('p-invalid');
    }

    return !error;
}

export const Validation = {
    mounted(el, binding) {
        el.__rules = binding.value?.rules || [];
        el.__binding = binding;
        el.__touched = false; // Initial mount par untouched rakha hai

        el.setAttribute('data-validate', 'true');

        // 🟢 Pass true to indicate automatic user listeners
        const runUserValidation = () => validateField(el, el.__binding || binding, true);
        el.__runUserValidation = runUserValidation;

        // User jab input par type ya interaction karay
        const handleInput = () => {
            el.__touched = true;
            runUserValidation();
        };

        // User jab blur karay (field choray)
        const handleBlur = () => {
            el.__touched = true;
            setTimeout(() => {
                runUserValidation();
            }, 50);
        };

        el.addEventListener('input', handleInput);
        el.addEventListener('change', handleInput);
        el.addEventListener('keyup', handleInput);
        el.addEventListener('focusout', handleBlur, true);

        // PrimeVue model watcher
        const vueInstance = el.__vueParentComponent;
        if (vueInstance && vueInstance.props && 'modelValue' in vueInstance.props) {
            vueInstance.ctx.$nextTick(() => {
                vueInstance.ctx.$watch(
                    () => vueInstance.props.modelValue,
                    (newVal, oldVal) => {
                        if (newVal !== oldVal && oldVal !== undefined) {
                            el.__touched = true;
                        }
                        if (el.__touched) {
                            setTimeout(() => runUserValidation(), 0);
                        }
                    },
                    { deep: true }
                );
            });
        }
    },

    updated(el, binding) {
        el.__rules = binding.value?.rules || [];
        el.__binding = binding;

        // Sirf tab chalega jab field touch ho chuki ho
        if (el.__touched && el.__runUserValidation) {
            setTimeout(() => {
                el.__runUserValidation();
            }, 0);
        }
    },

    unmounted(el) {
        if (el.__runUserValidation) {
            el.removeEventListener('input', el.__runUserValidation);
            el.removeEventListener('change', el.__runUserValidation);
            el.removeEventListener('keyup', el.__runUserValidation);
        }
    }
};

// 🔴 MODIFIED: Submit time sabhi fields ko force-validate (forceValidate = true) karta hai
export function validateForm(form) {
    let isValid = true;
    let firstInvalidField = null;
    const fields = form.querySelectorAll('[data-validate="true"]');

    fields.forEach((field) => {
        field.__touched = true; // Submit par sab field ko touched mark kar dein

        const binding = {
            value: {
                rules: field.__rules || []
            }
        };

        // Pass true as 3rd param to force validation on submit
        const fieldValid = validateField(field, binding, true);

        if (!fieldValid) {
            isValid = false;
            if (!firstInvalidField) {
                firstInvalidField = field;
            }
        }
    });

    if (firstInvalidField) {
        firstInvalidField.focus();
    }

    return isValid;
}

export function clearValidationErrors(formElement) {
    // Reset touched state for all fields
    const fields = formElement.querySelectorAll('[data-validate="true"]');
    fields.forEach((field) => {
        field.__touched = false;
    });

    // Remove all error spans
    const errorSpans = formElement.querySelectorAll('.validation-error');
    errorSpans.forEach((span) => span.remove());

    // Remove p-invalid class from inputs
    const invalidInputs = formElement.querySelectorAll('.p-invalid');
    invalidInputs.forEach((input) => input.classList.remove('p-invalid'));
}
